import React, { useState, useEffect } from 'react';
import Spinner from './components/common/Spinner';
import { Routes, Route, Navigate } from 'react-router-dom';
import { supabase } from './services/supabaseClient';
import InstallPWA from './components/common/InstallPWA';

// Import components
import Header from './components/Header';
import Footer from './components/Footer';
import RequireProfileCompletion from './components/Profile/RequireProfileCompletion';

// Import pages
import Login from './pages/Auth/Login';
import Register from './pages/Auth/Register';
import ResetPassword from './pages/Auth/ResetPassword';
import LandingPage from './pages/Landing';
import Dashboard from './pages/Dashboard/index';
import Profile from './pages/Profile/Profile';
import EditProfile from './pages/Profile/EditProfile';
import GigsList from './pages/Gigs/GigsList';
import GigDetails from './pages/Gigs/GigDetails';
import CreateGig from './pages/Gigs/CreateGig';
import EditGig from './pages/Gigs/EditGig';
import ArtistsList from './pages/Artists/ArtistsList';
import ArtistProfile from './pages/Artists/ArtistProfile';



import Settings from './pages/Settings/Settings';
import VenueMapPage from './pages/Map/VenueMapPage';
import NotFound from './pages/NotFound';

function App() {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initializeApp = async () => {
      try {
        console.log('Initializing app...');
        // Check session
        const { data, error } = await supabase.auth.getSession();
        if (error) {
          console.error('Error getting session:', error.message);
        } else {
          console.log('Session check result:', data.session ? 'Session found' : 'No session');
          setSession(data.session);
        }
      } catch (err) {
        console.error('Error initializing app:', err);
      } finally {
        console.log('App initialization complete, setting loading to false');
        setLoading(false);
      }
    };

    initializeApp();

    // Set up auth state listener
    const { data: authListener } = supabase.auth.onAuthStateChange(
      (event, session) => {
        console.log('Auth state changed:', event);
        setSession(session);
      }
    );

    return () => {
      if (authListener?.subscription) {
        authListener.subscription.unsubscribe();
      }
    };
  }, []);

  if (loading) {
    return (
      <div className="container mt-5">
        <Spinner message="Loading application..." fullPage={true} />
      </div>
    );
  }

  return (
    <div className="app">
      {session && <Header />}
      <main className={session ? "container" : "full-width"}>
        <Routes>
          {/* Public routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          
          {/* Landing page for non-authenticated users */}
          <Route 
            path="/" 
            element={
              session ? (
                <RequireProfileCompletion>
                  <Dashboard />
                </RequireProfileCompletion>
              ) : (
                <LandingPage />
              )
            } 
          />
          
          {/* Protected routes */}
          <Route 
            path="/profile" 
            element={
              session ? (
                <RequireProfileCompletion>
                  <Profile />
                </RequireProfileCompletion>
              ) : <Navigate to="/login" />
            } 
          />
          <Route 
            path="/profile/edit" 
            element={
              session ? <EditProfile /> : <Navigate to="/login" />
            } 
          />
          <Route 
            path="/gigs" 
            element={
              session ? (
                <RequireProfileCompletion>
                  <GigsList />
                </RequireProfileCompletion>
              ) : <Navigate to="/login" />
            } 
          />
          <Route 
            path="/gigs/:id" 
            element={
              session ? (
                <RequireProfileCompletion>
                  <GigDetails />
                </RequireProfileCompletion>
              ) : <Navigate to="/login" />
            } 
          />
          <Route 
            path="/gigs/create" 
            element={
              session ? (
                <RequireProfileCompletion>
                  <CreateGig />
                </RequireProfileCompletion>
              ) : <Navigate to="/login" />
            } 
          />
          <Route 
            path="/gigs/edit/:id" 
            element={
              session ? (
                <RequireProfileCompletion>
                  <EditGig />
                </RequireProfileCompletion>
              ) : <Navigate to="/login" />
            } 
          />
          <Route 
            path="/artists" 
            element={
              session ? (
                <RequireProfileCompletion>
                  <ArtistsList />
                </RequireProfileCompletion>
              ) : <Navigate to="/login" />
            } 
          />
          <Route 
            path="/artists/:id" 
            element={
              session ? (
                <RequireProfileCompletion>
                  <ArtistProfile />
                </RequireProfileCompletion>
              ) : <Navigate to="/login" />
            } 
          />

          <Route 
            path="/settings" 
            element={
              session ? (
                <RequireProfileCompletion>
                  <Settings />
                </RequireProfileCompletion>
              ) : <Navigate to="/login" />
            } 
          />
          
          {/* Map and location-based routes */}
          <Route 
            path="/venues-map" 
            element={
              session ? (
                <RequireProfileCompletion>
                  <VenueMapPage />
                </RequireProfileCompletion>
              ) : <Navigate to="/login" />
            } 
          />

          {/* 404 route */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </main>
      <Footer />
      <InstallPWA />
    </div>
  );
}

export default App;
